import { StrategyInformation } from "@models/clients/strategyInformation";
import { UserItem } from "@models/clients/userItem";
import { ClientRepo } from "@services/clients/client.repo";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { DropdownOptionsService } from "@services/clients/dropdownOptions.service";
import { ClientDetailsService } from "@services/clients/clients.details/clientdetails.service";
import * as moment from "moment";
import angular = require("angular");


export class ClientDetailsReportingController {
    public client: any;
    public users: UserItem[] = [];
    public combinedChecked: boolean = false;
    public localChecked: boolean = false;
    public internationalChecked: boolean = false;
    private previousCombinedChecked: boolean = false;
    public currencyList: string[] = ["MUR", "EUR", "GBP", "USD", "CHF"];
    public languageList: string[] = ["English", "French 1", "French 2"];
    public months: any[] = [];
    public showStrategyPanel: boolean = false;
    public strategyInformation: StrategyInformation = {
        isLocalStrategyEnabled: false,
        isInternationalStrategyEnabled: false,
        isCombinedStrategyEnabled: false,
        localStrategy: "",
        internationalStrategy: "",
    };
    public dropdownOptions: any = {};

    // Properties passed from parent
    public selectedAdmin: UserItem | null = null;
    public selectedRelationshipManager: UserItem | null = null;
    public selectedInvestmentOfficer: UserItem | null = null;
    public onSave: () => void;
    public isSaving: boolean = false;
    public lastSaved: Date | null = null;
    public onUpdateAdmin: (params: { user: UserItem }) => void;
    public onUpdateRelationshipManager: (params: { user: UserItem }) => void;
    public onUpdateInvestmentOfficer: (params: { user: UserItem }) => void;
    public onFilterUsers: (params: { searchText: string }) => UserItem[];
    public isLoadingUsers: boolean = true;

    // Benchmark settings
    public benchmarkColumnNames: string[] = [
        "Benchmark (%)",
        "Benchmark IPS (%)",
        "Benchmark FM (%)",
        "Return Target (%)",
    ];



    // Local loading state
    public isLoading: boolean = true;

    /** @ngInject */
    constructor(
        private clientRepo: ClientRepo,
        private clientCRMRepo: ClientCRMRepo,
        private dropdownOptionsService: DropdownOptionsService,
        private clientDetailsService: ClientDetailsService,
        private $scope: angular.IScope,
        private notifyjs: any
    ) {}

    public $onInit(): void {
        // Initialize months data
        this.months = moment.months().map((month, index) => {
            return {
                id: index + 1,
                name: month
            }
        });
        
        // Listen for dropdown options loaded event from parent
        this.$scope.$on('dropdownOptionsLoaded', (event: any, options: any) => {
            this.dropdownOptions = options;
        });
        
        // Try to get options immediately if they're already loaded
        this.dropdownOptionsService.getAllOptions()
          .then((options) => {
              this.dropdownOptions = options;
          })
          .catch((error) => {
              console.warn('Failed to get dropdown options immediately:', error);
          });

        // Sync Quill editor changes back to the client object whenever the strategy text changes
        this.$scope.$watch(() => this.strategyInformation.localStrategy, () => {
            this.populateStrategies();
        });
        this.$scope.$watch(() => this.strategyInformation.internationalStrategy, () => {
            this.populateStrategies();
        });

        // client uses a two-way (=) binding so $onChanges never fires for it.
        // Watch the reference so we populate strategy options when the async load resolves.
        this.$scope.$watch(() => this.client, (newVal) => {
            if (newVal) {
                this.isLoading = false;
                this.populateStrategyOptions();
            }
        });
    }

    strategiesPopulated() {
        let flag =
        this.strategyInformation.localStrategy !== null &&
        this.strategyInformation.localStrategy !== undefined &&
        this.strategyInformation.localStrategy.trim() !== "" &&
        this.strategyInformation.internationalStrategy !== null &&
        this.strategyInformation.internationalStrategy !== undefined &&
        this.strategyInformation.internationalStrategy.trim() !== "";

        return flag;
    }

    public setSelectedStrategy(): void {
        if (this.combinedChecked) {
            this.localChecked = true;
            this.internationalChecked = true;
        } else if (this.previousCombinedChecked) {
            // Combined was just unchecked — clear the auto-selected ones
            this.localChecked = false;
            this.internationalChecked = false;
        }

        this.previousCombinedChecked = this.combinedChecked;

        this.strategyInformation.isCombinedStrategyEnabled = this.combinedChecked;
        this.strategyInformation.isLocalStrategyEnabled = this.localChecked;
        this.strategyInformation.isInternationalStrategyEnabled = this.internationalChecked;

        this.showStrategyPanel = this.combinedChecked || this.localChecked || this.internationalChecked;

        this.populateStrategies();
    }

    public populateStrategies(): void {
        if (!this.client) return;
        
        this.client.isLocalStrategyEnabled = this.strategyInformation.isLocalStrategyEnabled;
        this.client.isInternationalStrategyEnabled = this.strategyInformation.isInternationalStrategyEnabled;
        this.client.isCombinedStrategyEnabled = this.strategyInformation.isCombinedStrategyEnabled;

        // Always sync text regardless of whether it's empty so that deselecting a strategy
        // properly clears the field on the client object sent to the backend.
        this.client.localStrategy = this.strategyInformation.localStrategy
            ? this.strategyInformation.localStrategy
                .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
                .replace(/  /g, "&nbsp;&nbsp;")
            : this.strategyInformation.localStrategy;

        this.client.internationalStrategy = this.strategyInformation.internationalStrategy
            ? this.strategyInformation.internationalStrategy
                .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
                .replace(/  /g, "&nbsp;&nbsp;")
            : this.strategyInformation.internationalStrategy;
    }

    public populateStrategyOptions(): void {
        if (!this.client) return;

        this.combinedChecked = !!this.client.isCombinedStrategyEnabled;
        this.localChecked = !!this.client.isLocalStrategyEnabled;
        this.internationalChecked = !!this.client.isInternationalStrategyEnabled;
        this.previousCombinedChecked = this.combinedChecked;

        this.strategyInformation.isCombinedStrategyEnabled = this.combinedChecked;
        this.strategyInformation.isLocalStrategyEnabled = this.localChecked;
        this.strategyInformation.isInternationalStrategyEnabled = this.internationalChecked;

        if (this.localChecked) {
            this.strategyInformation.localStrategy = this.client.localStrategy;
        }
        if (this.internationalChecked) {
            this.strategyInformation.internationalStrategy = this.client.internationalStrategy;
        }

        this.showStrategyPanel = this.combinedChecked || this.localChecked || this.internationalChecked;
    }

    public updateAdministeredBy(user: UserItem): void {
        if (user && this.onUpdateAdmin) {
            this.onUpdateAdmin({ user: user });
        }
    }

    public updateRelationshipManager(user: UserItem): void {
        if (user && this.onUpdateRelationshipManager) {
            this.onUpdateRelationshipManager({ user: user });
        }        
    }

    public updateInvestmentOfficer(user: UserItem): void {
        if (user && this.onUpdateInvestmentOfficer) {
            this.onUpdateInvestmentOfficer({ user: user });
        }        
    }

    public filterUsers(searchText: string): UserItem[] {
        if (this.onFilterUsers) {
            return this.onFilterUsers({ searchText: searchText });
        }
        return [];
    }

    // Helper method to check if client is individual
    public isIndividualClient(): boolean {
        return this.clientDetailsService.isIndividual(this.client);
    }



}

export const ClientDetailsReportingComponent = {
    template: require('./clientdetails.reporting.html'),
    style: require('./clientdetails.reporting.scss'),
    controller: ClientDetailsReportingController,
    controllerAs: '$ctrl',
    bindings: {
        client: '=',
        isViewOnly: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/reporting/clientdetails.reporting.ts